import React, { useState, useEffect, useCallback } from 'react';
import Modal from 'react-modal';
import { FaTimes, FaTrash, FaHandshake, FaGift } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import MembershipTrophy from './MembershipTrophy';
import { useFireAuth } from '../FireAuthContext';
import { fetchFriendData, removeFriend, sendTip } from './FriendFunctions';
import { getTradeIdOrCreate } from './TradeFunctions';
import './FriendCard.css';

const FriendCard = ({ isOpen, onRequestClose, friendId, updateUser }) => {
  const { user } = useFireAuth();
  const [friend, setFriend] = useState(null);
  const [tipAmount, setTipAmount] = useState('');
  const [showTipInput, setShowTipInput] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [canAffordTip, setCanAffordTip] = useState(true);
  const navigate = useNavigate();

  const fetchFriend = useCallback(async () => {
    try {
      const friendData = await fetchFriendData(friendId);
      setFriend(friendData);
    } catch (error) {
      console.error('Error fetching friend data:', error);
      setError(error.message);
    }
  }, [friendId]);

  useEffect(() => {
    if (friendId) {
      fetchFriend();
    }
  }, [fetchFriend, friendId]);

  useEffect(() => {
    const amount = parseFloat(tipAmount);
    setCanAffordTip(!isNaN(amount) && amount > 0 && amount <= user.money);
  }, [tipAmount, user.money]);

  const handleRemoveFriend = async () => {
    try {
      await removeFriend(user.uid, friendId);
      onRequestClose();
      await updateUser();
    } catch (error) {
      console.error('Error removing friend:', error);
      setError(error.message);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleTrade = async () => {
    try {
      const tradeId = await getTradeIdOrCreate(user.uid, friendId);
      if (tradeId) {
        navigate(`/portal/trade/${tradeId}`);
      } else {
        throw new Error('Failed to initiate trade.');
      }
    } catch (error) {
      console.error('Error initiating trade:', error);
      setError('Failed to initiate trade.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleTip = async () => {
    try {
      const amount = parseFloat(tipAmount);
      if (isNaN(amount) || amount <= 0 || amount > user.money) {
        throw new Error('Invalid tip amount');
      }
      await sendTip(user.uid, friendId, amount);
      setTipAmount('');
      setShowTipInput(false);
      await updateUser();
      await fetchFriend();
      setSuccessMessage(`💴${amount.toFixed(2)} sent to ${friend.name}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error tipping money:', error);
      setError(error.message);
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (!friend) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Friend Profile"
      className="friend-card-modal"
      overlayClassName="friend-card-modal-overlay"
    >
      <div className="friend-card-modal-content">
        <button onClick={onRequestClose} className="friend-card-close-button">
          <FaTimes />
        </button>
        <div className="friend-card-columns">
          <div className="friend-card-left">
            <div className="friend-card-box">
              <img src={friend.profilePicture || 'default-avatar.png'} alt="Profile" className="friend-card-picture" />
              <div className="friend-card-details">
                <h3>{friend.name}</h3>
                <p>
                  <strong>Club #:</strong> {friend.membershipNumber} | 
                  <span className="membership-status">
                    {friend.membershipStatus} <MembershipTrophy status={friend.membershipStatus} />
                  </span>
                </p>
                <p><strong>Member Since:</strong> {formatDate(friend.createdAt)}</p>
                <p><strong>Status:</strong> {friend.status}</p>
                <p><strong>Money:</strong> 💴 {friend.money.toFixed(2)}</p>
                <p><strong>HahumCoin:</strong> 🪙 {friend.crypto?.hahumCoin.toFixed(2) || 0}</p>
                <p><strong>Games Played:</strong> {friend.gamesPlayed || 0}</p>
              </div>
            </div>
          </div>
          <div className="friend-card-right">
            <div className="friend-card-box">
              <h3>Bio</h3>
              <p>{friend.bio || 'No bio available'}</p>
            </div>
            <div className="friend-card-box">
              <div className="friend-card-actions">
                <button onClick={handleRemoveFriend} className="friend-card-action-button">
                  <FaTrash /> Remove as Friend
                </button>
                <button onClick={handleTrade} className="friend-card-action-button">
                  <FaHandshake /> Trade
                </button>
                <button onClick={() => setShowTipInput(!showTipInput)} className="friend-card-action-button">
                  <FaGift /> Tip
                </button>
                {showTipInput && (
                  <div className="friend-card-tip-input">
                    <input
                      type="number"
                      value={tipAmount}
                      onChange={(e) => setTipAmount(e.target.value)}
                      placeholder={`💴 Max: ${user.money.toFixed(2)}`}
                    />
                    <button 
                      onClick={handleTip} 
                      className={`friend-card-action-button ${!canAffordTip ? 'disabled' : ''}`}
                      disabled={!canAffordTip}
                    >
                      Send
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {error && <p className="error-message">{error}</p>}
        {successMessage && <p className="success-message">{successMessage}</p>}
      </div>
    </Modal>
  );
};

export default FriendCard;
